/* ──────────────────────────────────────────────────────────────
 * FlipButton — toggles between geometry view states
 *
 * View sequences:
 *   - A1, C1, BYD EV Solid: iso ↔ sxh
 *   - B1: iso ↔ sxh
 *   - D1, BYD EV Voided: iso → axh → sxh
 *
 * Icons represent the currently displayed geometry view:
 *   - iso: Full 3D geometry
 *   - axh: Axial cross-section
 *   - sxh: Side cross-section
 *
 * On click, the parent advances to the next view through the
 * existing onFlip callback.
 * ────────────────────────────────────────────────────────────── */

import React from 'react';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import ThreeDRotationOutlinedIcon from
  '@mui/icons-material/ThreeDRotationOutlined';
import DonutLargeOutlinedIcon from
  '@mui/icons-material/DonutLargeOutlined';
import VerticalSplitOutlinedIcon from
  '@mui/icons-material/VerticalSplitOutlined';

import type { GeometryView } from '@/types/geometry';

/** Human-readable labels for each geometry view state. */
const VIEW_LABELS: Record<GeometryView, string> = {
  iso: 'ISO full 3D view',
  sxh: 'Side cross-section',
  axh: 'Axial cross-section',
};

interface FlipButtonProps {
  /** Currently displayed geometry view. */
  currentView: GeometryView;

  /** Whether geometry view switching is disabled. */
  disabled?: boolean;

  /** Handler responsible for advancing to the next view. */
  onFlip: () => void;
}

/**
 * Returns the icon representing the currently displayed view.
 *
 * The icon does not control view sequencing. The sequence remains
 * fully managed by the existing parent onFlip handler.
 */
function getViewIcon(currentView: GeometryView): React.ReactElement {
  switch (currentView) {
    case 'iso':
      return (
        <ThreeDRotationOutlinedIcon
          sx={{
            fontSize: 19,
          }}
        />
      );

    case 'axh':
      /*
       * A concentric circular icon represents the axial section
       * through the core, rubber, and outer sleeve of the bush.
       */
      return (
        <DonutLargeOutlinedIcon
          sx={{
            fontSize: 20,
          }}
        />
      );

    case 'sxh':
      /*
       * A divided geometry icon represents a longitudinal or
       * side section through the bush.
       */
      return (
        <VerticalSplitOutlinedIcon
          sx={{
            fontSize: 19,
          }}
        />
      );

    default:
      return (
        <ThreeDRotationOutlinedIcon
          sx={{
            fontSize: 19,
          }}
        />
      );
  }
}

const FlipButton: React.FC<FlipButtonProps> = ({
  currentView,
  disabled = false,
  onFlip,
}) => {
  const currentViewLabel = VIEW_LABELS[currentView];

  const tooltip = disabled
    ? 'Geometry view switching is unavailable'
    : `Current: ${currentViewLabel}. Click to change view`;

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
    >
      {/*
       * The span keeps the tooltip functional when the MUI
       * IconButton is disabled.
       */}
      <span>
        <IconButton
          type="button"
          size="small"
          disabled={disabled}
          onClick={onFlip}
          aria-label={`Change geometry view. Current view: ${currentViewLabel}`}
          sx={{
            width: 32,
            height: 32,
            padding: 0.5,
            color: 'info.main',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            transition: [
              'color 120ms ease',
              'border-color 120ms ease',
              'background-color 120ms ease',
              'box-shadow 120ms ease',
            ].join(', '),

            '&:hover': {
              color: 'info.dark',
              bgcolor: 'action.hover',
              borderColor: 'info.main',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
            },

            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'info.main',
              outlineOffset: 2,
            },

            '&.Mui-disabled': {
              color: 'action.disabled',
              bgcolor: 'action.disabledBackground',
              borderColor: 'grey.200',
            },
          }}
        >
          {getViewIcon(currentView)}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default React.memo(FlipButton);